import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { SocketProvider } from './contexts/SocketContext';
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import { AiCreditsProvider } from './contexts/AiCreditsContext';
import AiCreditsReturnPage from './pages/ai-credits/AiCreditsReturnPage';

// Layouts
import AuthLayout from './components/layout/AuthLayout';
import DashboardLayout from './components/layout/DashboardLayout';
import AdminLayout from './components/layout/AdminLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import SsoPage from './pages/auth/SsoPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';

// Dashboard Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import CampaignsPage from './pages/campaigns/CampaignsPage';
import ShortLinksPage from './pages/short-links/ShortLinksPage';
import AudiencesPage from './pages/audiences/AudiencesPage';
import ContactsPage from './pages/contacts/ContactsPage';
import CampaignDetailPage from './pages/campaigns/CampaignDetailPage';
import TrafficManagerPage from './pages/campaigns/TrafficManagerPage';
import CampaignFormPage from './pages/campaigns/CampaignFormPage';
import NewCampaignPage from './pages/campaigns/NewCampaignPage';
// KeywordsPage import removed - redirects to /campaigns
import CompetitorsPage from './pages/competitors/CompetitorsPage';
import CompetitorDetailPage from './pages/competitors/CompetitorDetailPage';
import SiteExplorerPage from './pages/competitors/SiteExplorerPage';
import KeywordsExplorerPage from './pages/competitors/KeywordsExplorerPage';
import BacklinksPage from './pages/competitors/BacklinksPage';
import RankTrackerPage from './pages/competitors/RankTrackerPage';
import SiteAuditPage from './pages/competitors/SiteAuditPage';
import ContentPage from './pages/content/ContentPage';
import ContentGeneratorPage from './pages/content/ContentGeneratorPage';
import ContentHubPage from './pages/content/ContentHubPage';
import BlogSeoPage from './pages/blog-seo/BlogSeoPage';
import ContentCreatorPage from './pages/content/ContentCreatorPage';
import IntegrationsPage from './pages/content/IntegrationsPage';
import ContentAnalyticsPage from './pages/content/ContentAnalyticsPage';
// AutomationPage import removed - redirects to /campaigns
import FlowAutomationPage from './pages/automation/FlowAutomationPage';
import WorkflowsPage from './pages/workflows/WorkflowsPage';
import LandingPagesPage from './pages/landing-pages/LandingPagesPage';
import LandingPageBuilder from './pages/landing-pages/LandingPageBuilderNew';
// ReportsPage removed - reports merged into dashboard
import SubscriptionPage from './pages/subscription/SubscriptionPage';
import SubscriptionSuccessPage from './pages/subscription/SubscriptionSuccessPage';
import SubscriptionCancelPage from './pages/subscription/SubscriptionCancelPage';
import SubscriptionPendingPage from './pages/subscription/SubscriptionPendingPage';
import SettingsPage from './pages/settings/SettingsPage';
import BusinessProfilePage from './pages/settings/BusinessProfilePage';
import NotificationsPage from './pages/notifications/NotificationsPage';
import ImageStudioPage from './pages/image-studio/ImageStudioPage';
import ImageEditorPage from './pages/image-studio/ImageEditorPage';
import VideoStudioPage from './pages/video-studio/VideoStudioPage';
import LiveAutomationPage from './pages/live-automation/LiveAutomationPage';
import ModuleGate from './components/ModuleGate';

// Admin Pages
import AdminDashboardPage from './pages/admin/AdminDashboardPage';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminPlansPage from './pages/admin/AdminPlansPage';
import AdminSubscriptionsPage from './pages/admin/AdminSubscriptionsPage';
import AdminCompaniesPage from './pages/admin/AdminCompaniesPage';
import AdminAiCreditsPage from './pages/admin/AdminAiCreditsPage';
import AdminAffiliatesPage from './pages/admin/AdminAffiliatesPage';

// Onboarding
import OnboardingWizard from './pages/onboarding/OnboardingWizard';

// Loading Component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
      <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode; requiresProject?: boolean; allowWhenPaymentRequired?: boolean }> = ({ children, requiresProject = true, allowWhenPaymentRequired = false }) => {
  const { isAuthenticated, isLoading: authLoading, needsOnboarding, user } = useAuth();
  const { isLoading: projectLoading, hasNoProjects } = useProject();

  if (authLoading || projectLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Bloqueia acesso quando usuário escolheu plano pago mas não cadastrou cartão
  if (!allowWhenPaymentRequired && (user as any)?.requires_payment) {
    return <Navigate to="/subscription/pending" replace />;
  }

  // Only redirect to onboarding if user has no projects AND onboarding is not completed
  // This prevents redirect loops after completing onboarding (race condition with project loading)
  if (requiresProject && hasNoProjects && needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
};

// Admin Route Component
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !['admin', 'super_admin'].includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  return (
    <Routes>
      {/* Public Routes */}
      {/* SSO (link do painel do restaurante) — fora do PublicRoute: troca de conta mesmo já logado */}
      <Route path="/sso" element={<SsoPage />} />
      <Route element={<PublicRoute><AuthLayout /></PublicRoute>}>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/signup" element={<RegisterPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
      </Route>

      {/* Onboarding Route (protected but no layout, doesn't require project) */}
      <Route path="/onboarding" element={<ProtectedRoute requiresProject={false}><OnboardingWizard /></ProtectedRoute>} />

      {/* Volta do pagamento de créditos de IA (abre na aba do Stripe, sem o menu) */}
      <Route path="/ai-credits/return" element={<ProtectedRoute requiresProject={false} allowWhenPaymentRequired><AiCreditsReturnPage /></ProtectedRoute>} />

      {/* Page Builder Routes (full screen, no dashboard layout) */}
      <Route path="/landing-pages/new" element={<ProtectedRoute><LandingPageBuilder /></ProtectedRoute>} />
      <Route path="/landing-pages/:id/edit" element={<ProtectedRoute><LandingPageBuilder /></ProtectedRoute>} />

      {/* Workflow Builder Routes (full screen, no dashboard layout) */}
      <Route path="/workflows/new" element={<ProtectedRoute><FlowAutomationPage /></ProtectedRoute>} />
      <Route path="/workflows/:id" element={<ProtectedRoute><FlowAutomationPage /></ProtectedRoute>} />

      {/* Image Editor Routes (full screen, no dashboard layout) */}
      <Route path="/image-studio/editor" element={<ProtectedRoute><ImageEditorPage /></ProtectedRoute>} />
      <Route path="/image-studio/editor/:id" element={<ProtectedRoute><ImageEditorPage /></ProtectedRoute>} />

      {/* Content Creator Route (full screen, no dashboard layout) */}
      <Route path="/content/create" element={<ProtectedRoute><ContentCreatorPage /></ProtectedRoute>} />
      <Route path="/content/edit/:id" element={<ProtectedRoute><ContentCreatorPage /></ProtectedRoute>} />

      {/* Subscription Callback Routes (full screen, no dashboard layout) */}
      <Route path="/subscription/success" element={<ProtectedRoute requiresProject={false}><SubscriptionSuccessPage /></ProtectedRoute>} />
      <Route path="/subscription/cancel" element={<ProtectedRoute requiresProject={false} allowWhenPaymentRequired><SubscriptionCancelPage /></ProtectedRoute>} />
      <Route path="/subscription/pending" element={<ProtectedRoute requiresProject={false} allowWhenPaymentRequired><SubscriptionPendingPage /></ProtectedRoute>} />

      {/* Protected Routes */}
      <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/campaigns/new" element={<NewCampaignPage />} />
        <Route path="/short-links" element={<ShortLinksPage />} />
        <Route path="/campaigns/autopilot" element={<TrafficManagerPage />} />
        <Route path="/campaigns/:id" element={<CampaignDetailPage />} />
        <Route path="/campaigns/:id/edit" element={<NewCampaignPage />} />
        <Route path="/keywords" element={<Navigate to="/campaigns" replace />} />
        <Route path="/competitors" element={<CompetitorsPage />} />
        {/* Ferramentas de Concorrentes: cada uma é uma aba (SectionTabs) do item Concorrentes */}
        <Route path="/competitors/site-explorer" element={<SiteExplorerPage />} />
        <Route path="/competitors/keywords-explorer" element={<KeywordsExplorerPage />} />
        <Route path="/competitors/backlinks" element={<BacklinksPage />} />
        <Route path="/competitors/rank-tracker" element={<RankTrackerPage />} />
        <Route path="/competitors/site-audit" element={<SiteAuditPage />} />
        {/* Dynamic :id route for competitor details */}
        <Route path="/competitors/:id" element={<CompetitorDetailPage />} />
        <Route path="/content" element={<ContentHubPage />} />
        <Route path="/content/old" element={<ContentPage />} />
        <Route path="/content/generate" element={<ContentGeneratorPage />} />
        <Route path="/content/integrations" element={<IntegrationsPage />} />
        <Route path="/content/calendar" element={<ContentHubPage />} />
        <Route path="/content/analytics" element={<ContentAnalyticsPage />} />
        <Route path="/content/all" element={<ContentHubPage />} />
        <Route path="/blog-seo" element={<BlogSeoPage />} />
        <Route path="/blog-seo/:tab" element={<BlogSeoPage />} />
        <Route path="/landing-pages" element={<LandingPagesPage />} />
        <Route path="/audiences" element={<AudiencesPage />} />
        <Route path="/contacts" element={<ModuleGate module="contacts" name="Contatos"><ContactsPage /></ModuleGate>} />
        <Route path="/rastreamento" element={<Navigate to="/audiences" replace />} />
        <Route path="/workflows" element={<WorkflowsPage />} />
        <Route path="/image-studio" element={<ImageStudioPage />} />
        <Route path="/video-studio" element={<VideoStudioPage />} />
        <Route path="/live-automation" element={<ModuleGate module="liveAutomation" name="Ao vivo"><LiveAutomationPage /></ModuleGate>} />
        <Route path="/automation" element={<Navigate to="/campaigns" replace />} />
        <Route path="/social" element={<Navigate to="/content" replace />} />
        <Route path="/social/*" element={<Navigate to="/content" replace />} />
        <Route path="/reports" element={<Navigate to="/dashboard" replace />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/business-profile" element={<BusinessProfilePage />} />
        <Route path="/settings/:tab" element={<SettingsPage />} />
      </Route>

      {/* Admin Routes */}
      <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
        <Route path="/admin" element={<AdminDashboardPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/companies" element={<AdminCompaniesPage />} />
        <Route path="/admin/plans" element={<AdminPlansPage />} />
        <Route path="/admin/subscriptions" element={<AdminSubscriptionsPage />} />
        <Route path="/admin/affiliates" element={<AdminAffiliatesPage />} />
        <Route path="/admin/ai-credits" element={<AdminAiCreditsPage />} />
      </Route>

      {/* Default Routes */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
};

const ThemedToaster = () => {
  const { isDark } = useTheme();

  return (
    <Toaster
      position="bottom-right"
      containerStyle={{
        bottom: 20,
        right: 20
      }}
      toastOptions={{
        duration: 4000,
        style: {
          background: isDark ? '#1f2937' : '#fff',
          color: isDark ? '#f3f4f6' : '#374151',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          borderRadius: '0.5rem',
          padding: '1rem',
          maxWidth: '400px'
        },
        success: {
          iconTheme: {
            primary: '#10B981',
            secondary: isDark ? '#1f2937' : '#fff'
          }
        },
        error: {
          iconTheme: {
            primary: '#EF4444',
            secondary: isDark ? '#1f2937' : '#fff'
          }
        }
      }}
    />
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ProjectProvider>
            <SocketProvider>
              <AiCreditsProvider>
                <AppRoutes />
                <ThemedToaster />
              </AiCreditsProvider>
            </SocketProvider>
          </ProjectProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
};

export default App;
